import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Trophy, Sparkles, ArrowUpRight, TrendingUp, Users, Coins } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const MEDAL = {
  1: { emoji: "01", tone: "#E8B96C", label: "First" },
  2: { emoji: "02", tone: "#D4B896", label: "Second" },
  3: { emoji: "03", tone: "#C4A882", label: "Third" },
};

export default function Leaderboard() {
  const [params] = useSearchParams();
  const refFromLink = params.get("ref");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/leaderboard?limit=25`);
        if (!alive) return;
        setData(r.data);
        setStatus("ok");
      } catch (e) {
        if (!alive) return;
        setStatus("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const signupHref = refFromLink ? `/signup?ref=${encodeURIComponent(refFromLink)}` : "/signup";
  const entries = data?.entries || [];
  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div className="min-h-screen bg-[#0A0806] text-[#F4EAD5] relative overflow-hidden">
      {/* ambient glow */}
      <div className="absolute inset-0 pointer-events-none opacity-70" aria-hidden style={{
        background:
          "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(232, 185, 108, 0.08) 0%, transparent 60%), radial-gradient(ellipse 45% 40% at 100% 100%, rgba(232, 185, 108, 0.05) 0%, transparent 60%)",
      }} />
      {/* paper grain */}
      <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-overlay" aria-hidden style={{
        backgroundImage: "radial-gradient(rgba(255,220,180,0.15) 1px, transparent 1px)",
        backgroundSize: "3px 3px",
      }} />

      {/* header */}
      <header className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-8 md:pt-10 flex items-center justify-between">
        <Link to="/" data-testid="leaderboard-brand-link" className="font-serif text-2xl tracking-tight text-[#F4EAD5]" style={{ fontFamily: "'Fraunces', serif" }}>
          Quill<span style={{ color: "#E8B96C" }}>.</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="leaderboard-login-link"
            className="text-[11px] uppercase tracking-[0.22em] text-[#8a7565] hover:text-[#E8B96C] transition-colors font-mono"
          >
            Sign in
          </Link>
          <Link
            to={signupHref}
            data-testid="leaderboard-signup-cta"
            className="text-[12px] px-4 py-2 rounded-full font-medium transition-all hover:-translate-y-0.5"
            style={{ background: "linear-gradient(180deg, #FFF6E5, #FFD9B5)", color: "#1F1B17" }}
          >
            Start earning →
          </Link>
        </div>
      </header>

      {/* hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-8">
        <div className="pl-6 md:pl-8 relative">
          <div aria-hidden className="absolute left-0 top-0 bottom-0 w-px pointer-events-none" style={{
            background: "linear-gradient(180deg, transparent, hsl(0 65% 55% / 0.6) 20%, hsl(0 65% 55% / 0.75) 50%, hsl(0 65% 55% / 0.55) 80%, transparent)",
          }} />
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#E8B96C] flex items-center gap-2 font-mono">
            <Trophy size={13} /> Referral Wall
          </div>
          <h1 className="mt-4 text-4xl md:text-6xl leading-[1.02] tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
            Top referrers of<br />the crypto-creator era.
          </h1>
          <p className="mt-5 text-[#c9b8a8] text-base md:text-lg max-w-xl leading-relaxed">
            Every Quill user gets a share link. Every paid signup earns them <span className="text-[#F4EAD5]">30% recurring</span>{" "}
            USDC on Base — for as long as the referral stays a customer. Here's who's winning.
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10">
          <StatChip icon={<Coins size={14} />} label="Total credited" value={data ? fmtMoney(data.total_paid_usd) : "…"} testId="stat-total-paid" />
          <StatChip icon={<Users size={14} />} label="Paying referrals" value={data ? (data.total_conversions ?? 0).toLocaleString() : "…"} testId="stat-conversions" />
          <StatChip icon={<TrendingUp size={14} />} label="Active referrers" value={data ? (data.total_referrers ?? 0).toLocaleString() : "…"} testId="stat-referrers" />
        </div>
      </section>

      {/* podium + table */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
        {status === "loading" && <LoadingRow />}
        {status === "error" && <Callout msg="Couldn't load the wall — try again in a bit." />}
        {status === "ok" && entries.length === 0 && (
          <EmptyState signupHref={signupHref} />
        )}

        {status === "ok" && podium.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="leaderboard-podium">
            {podium.map((e) => (
              <PodiumCard key={e.rank} entry={e} />
            ))}
          </div>
        )}

        {status === "ok" && rest.length > 0 && (
          <div className="mt-10 relative rounded-sm overflow-hidden" style={{
            background: "linear-gradient(180deg, #241a14 0%, #1e1712 100%)",
            border: "1px solid #35271e",
            boxShadow: "0 24px 60px -30px rgba(0,0,0,0.6)",
          }} data-testid="leaderboard-table">
            <div aria-hidden className="absolute left-[18px] top-3 bottom-3 w-px pointer-events-none" style={{
              background: "linear-gradient(180deg, transparent, hsl(0 65% 55% / 0.45) 20%, hsl(0 65% 55% / 0.55) 50%, hsl(0 65% 55% / 0.35) 80%, transparent)",
            }} />
            <div className="overflow-x-auto">
            <table className="w-full min-w-[520px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.20em] text-[#8a7565] border-b border-[#35271e]">
                  <th className="pl-10 pr-3 py-4 text-left font-normal">Rank</th>
                  <th className="px-3 py-4 text-left font-normal">Referrer</th>
                  <th className="px-3 py-4 text-right font-normal">Referrals</th>
                  <th className="px-3 py-4 text-right font-normal pr-6 md:pr-8">Earned</th>
                </tr>
              </thead>
              <tbody>
                {rest.map((e) => (
                  <tr key={e.rank} className="border-b border-[#35271e] last:border-b-0 hover:bg-[#2a1f18] transition-colors" data-testid={`leaderboard-row-${e.rank}`}>
                    <td className="pl-10 pr-3 py-4 font-mono text-sm text-[#8a7565]">#{String(e.rank).padStart(2, "0")}</td>
                    <td className="px-3 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={e.display_name} picture={e.picture} size={32} />
                        <div>
                          <div className="text-sm text-[#F4EAD5]">{e.display_name}</div>
                          {e.member_since && (
                            <div className="text-[10px] text-[#71615a] font-mono uppercase tracking-wider">Since {fmtDate(e.member_since)}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-right font-mono text-sm text-[#c9b8a8]">{e.conversions}</td>
                    <td className="px-3 py-4 text-right font-mono text-sm text-[#F4EAD5] pr-6 md:pr-8">{fmtMoney(e.lifetime_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        {status === "ok" && entries.length > 0 && (
          <div className="mt-16 md:mt-24 text-center">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#E8B96C] font-mono">Your turn</div>
            <h2 className="mt-3 text-3xl md:text-4xl tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
              Get on the wall.
            </h2>
            <p className="mt-3 text-[#c9b8a8] max-w-lg mx-auto leading-relaxed">
              Sign up in 30 seconds, grab your share link, and the next paying friend puts you on the leaderboard.
            </p>
            <Link
              to={signupHref}
              data-testid="leaderboard-bottom-cta"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all hover:-translate-y-0.5"
              style={{ background: "linear-gradient(180deg, #FFF6E5, #FFD9B5)", color: "#1F1B17" }}
            >
              <Sparkles size={14} /> Start referring
              <ArrowUpRight size={14} />
            </Link>
          </div>
        )}
      </section>

      {/* footer strip */}
      <footer className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 py-8 border-t border-[#35271e] flex flex-wrap items-center justify-between gap-4">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#71615a] font-mono">
          Refreshed live · 30% recurring · Paid in USDC on Base
        </div>
        <Link to="/" data-testid="leaderboard-home-link" className="text-[11px] uppercase tracking-[0.24em] text-[#8a7565] hover:text-[#E8B96C] transition-colors font-mono">
          ← Back to Quill
        </Link>
      </footer>
    </div>
  );
}

function StatChip({ icon, label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-sm px-5 py-4 flex items-center gap-3"
      style={{
        background: "linear-gradient(180deg, #241a14 0%, #1e1712 100%)",
        border: "1px solid #35271e",
      }}
    >
      <div className="p-2 rounded-full" style={{ background: "rgba(232, 185, 108, 0.1)", color: "#E8B96C" }}>{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8a7565] font-mono">{label}</div>
        <div className="text-lg md:text-xl mt-0.5" style={{ fontFamily: "'Fraunces', serif" }}>{value}</div>
      </div>
    </div>
  );
}

function Avatar({ name, picture, size = 40 }) {
  if (picture) {
    return <img src={picture} alt={name} width={size} height={size} className="rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      className="rounded-full flex items-center justify-center font-mono text-sm shrink-0"
      style={{
        width: size,
        height: size,
        background: "linear-gradient(180deg, rgba(232, 185, 108, 0.2), rgba(232, 185, 108, 0.05))",
        border: "1px solid rgba(232, 185, 108, 0.25)",
        color: "#E8B96C",
      }}
    >
      {initial}
    </div>
  );
}

function PodiumCard({ entry }) {
  const m = MEDAL[entry.rank] || MEDAL[3];
  const featured = entry.rank === 1;
  return (
    <div
      data-testid={`leaderboard-podium-${entry.rank}`}
      className={`relative rounded-sm p-6 md:p-7 overflow-hidden ${featured ? "md:-translate-y-2" : ""} transition-transform hover:-translate-y-1`}
      style={{
        background: featured
          ? "linear-gradient(180deg, #2d1f16 0%, #211812 100%)"
          : "linear-gradient(180deg, #241a14 0%, #1e1712 100%)",
        border: `1px solid ${featured ? "rgba(232, 185, 108, 0.35)" : "#35271e"}`,
        boxShadow: featured ? "0 24px 60px -30px rgba(232, 185, 108, 0.35)" : "0 20px 40px -30px rgba(0,0,0,0.5)",
      }}
    >
      <div aria-hidden className="absolute left-4 top-4 bottom-4 w-px pointer-events-none" style={{
        background: "linear-gradient(180deg, transparent, hsl(0 65% 55% / 0.55) 20%, hsl(0 65% 55% / 0.7) 50%, hsl(0 65% 55% / 0.5) 80%, transparent)",
      }} />
      <div className="pl-6 relative">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: m.tone }}>
            {m.label} · #{m.emoji}
          </div>
          {featured && <Trophy size={16} style={{ color: m.tone }} />}
        </div>
        <div className="flex items-center gap-3 mb-5">
          <Avatar name={entry.display_name} picture={entry.picture} size={44} />
          <div className="min-w-0">
            <div className="text-lg md:text-xl truncate" style={{ fontFamily: "'Fraunces', serif" }}>{entry.display_name}</div>
            {entry.member_since && (
              <div className="text-[10px] text-[#71615a] font-mono uppercase tracking-wider">Since {fmtDate(entry.member_since)}</div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-[#35271e]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71615a] font-mono">Referrals</div>
            <div className="mt-1 text-xl font-mono text-[#F4EAD5]">{entry.conversions}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71615a] font-mono">Earned</div>
            <div className="mt-1 text-xl font-mono" style={{ color: m.tone }}>{fmtMoney(entry.lifetime_usd)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ signupHref }) {
  return (
    <div
      className="relative rounded-sm p-10 md:p-16 text-center overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #241a14 0%, #1e1712 100%)",
        border: "1px solid #35271e",
      }}
      data-testid="leaderboard-empty-state"
    >
      <div aria-hidden className="absolute left-6 top-6 bottom-6 w-px pointer-events-none" style={{
        background: "linear-gradient(180deg, transparent, hsl(0 65% 55% / 0.55) 20%, hsl(0 65% 55% / 0.7) 50%, hsl(0 65% 55% / 0.5) 80%, transparent)",
      }} />
      <Trophy size={40} className="mx-auto mb-5" style={{ color: "#E8B96C" }} />
      <div className="text-[11px] uppercase tracking-[0.24em] text-[#E8B96C] font-mono">The wall's opening soon</div>
      <h3 className="mt-4 text-3xl md:text-4xl tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
        Be first on the leaderboard.
      </h3>
      <p className="mt-4 text-[#c9b8a8] max-w-md mx-auto leading-relaxed">
        We just opened Quill's referral program. Sign up now, share your link, and the first paying referral
        puts your name on the top of this wall for good.
      </p>
      <Link
        to={signupHref}
        data-testid="leaderboard-empty-cta"
        className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all hover:-translate-y-0.5"
        style={{ background: "linear-gradient(180deg, #FFF6E5, #FFD9B5)", color: "#1F1B17" }}
      >
        <Sparkles size={14} /> Claim rank #01
        <ArrowUpRight size={14} />
      </Link>
    </div>
  );
}

function LoadingRow() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-sm h-40 animate-pulse" style={{ background: "linear-gradient(180deg, #241a14 0%, #1e1712 100%)", border: "1px solid #35271e" }} />
      ))}
    </div>
  );
}

function Callout({ msg }) {
  return (
    <div className="border rounded-sm p-6 text-center text-[#c9b8a8]" style={{ background: "#241a14", borderColor: "#35271e" }} data-testid="leaderboard-error">
      {msg}
    </div>
  );
}
